/**
 * Generated text as the model meant it. Papers and cards arrive with a little markdown —
 * the committed pipeline cache carries `**bold**`, `x^2`, `` `code` ``, line breaks and, in
 * papers, tables; the v2 deployed check showed a frequency table as one line of pipes and
 * `10^8` with its caret. Only that much is read; everything else stays literal text, and
 * nothing is ever set as HTML. `$` is currency here ("$5 of $80") and `_` a blank to fill
 * ("(x + _)"), so neither is markup.
 */

import { Fragment } from "react";
import type { ReactNode } from "react";

// A table's separator row: | --- | :---: |
const SEPARATOR = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
// **bold**, `code`, or a power: a base that ends in a digit, letter or ")" then ^ and
// a bracketed group, or a signed number, or a word — 10^8, x^2, 2^n, 10^-3, (a+b)^(n+1).
const INLINE =
  /\*\*([^*\n]+)\*\*|`([^`\n]+)`|(?<=[\w)])\^(?:\(([^()\n]+)\)|(-?\d+(?:\.\d+)?|[A-Za-z]+))/g;

type Block = { kind: "text"; lines: string[] } | { kind: "table"; rows: string[][] };

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

/** A table the model wrote on one line — rows run together as "| a | b | | c | d |" — is
 * split back into rows at the separator's width. */
function unfold(line: string): string[] {
  const start = line.indexOf("|");
  const sep = /\|(\s*:?-{3,}:?\s*\|)+/.exec(line);
  if (start < 0 || !sep) return [line];
  const width = sep[0].split("|").length - 2;
  const cellsAll = line.slice(start).trim().replace(/^\|/, "").replace(/\|$/, "").split("|");
  const rows: string[] = [];
  // Between rows the model leaves "| |": an empty cell. Every row is width cells plus one.
  for (let i = 0; i < cellsAll.length; i += width + 1)
    rows.push(`|${cellsAll.slice(i, i + width).join("|")}|`);
  const head = line.slice(0, start).trim();
  return [...(head ? [head] : []), ...rows];
}

export function blocksOf(text: string): Block[] {
  const lines = text
    .split("\n")
    .flatMap((l) =>
      l.includes("|") &&
      /-{3,}/.test(l) &&
      !SEPARATOR.test(l.trim()) &&
      (l.match(/\|/g)?.length ?? 0) > 4
        ? unfold(l)
        : [l],
    );
  const blocks: Block[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const next = lines[i + 1];
    if (line.trim().startsWith("|") && next !== undefined && SEPARATOR.test(next.trim())) {
      const rows = [cells(line)];
      i += 2;
      while (i < lines.length && lines[i]!.trim().startsWith("|")) rows.push(cells(lines[i++]!));
      i -= 1;
      blocks.push({ kind: "table", rows });
      continue;
    }
    const last = blocks.at(-1);
    if (line.trim() === "") {
      if (last?.kind === "text" && last.lines.length > 0) blocks.push({ kind: "text", lines: [] });
      continue;
    }
    if (last?.kind === "text") last.lines.push(line);
    else blocks.push({ kind: "text", lines: [line] });
  }
  return blocks.filter((b) => b.kind === "table" || b.lines.length > 0);
}

/** Bold, code and powers inside one line. */
export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index > at) out.push(text.slice(at, m.index));
    const key = m.index;
    if (m[1] !== undefined) out.push(<strong key={key}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<code key={key}>{m[2]}</code>);
    else out.push(<sup key={key}>{m[3] ?? m[4]}</sup>);
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

const withBreaks = (lines: string[]) =>
  lines.map((l, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {inline(l)}
    </Fragment>
  ));

/**
 * `block` (question text, mark schemes): paragraphs and tables, so it must sit in a `div`.
 * Otherwise inline only — line breaks, bold, code, powers — for places that take phrasing
 * content, like a card inside its button; a table there reads as its rows, one per line.
 */
export function RichText({ text, block = false }: { text: string; block?: boolean }) {
  const blocks = blocksOf(text);
  if (!block) {
    const lines = blocks.flatMap((b) =>
      b.kind === "text" ? b.lines : b.rows.map((r) => r.join(" · ")),
    );
    return <>{withBreaks(lines)}</>;
  }
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === "text" ? (
          <p className="rich__p" key={i}>
            {withBreaks(b.lines)}
          </p>
        ) : (
          <table className="rich__table" key={i}>
            <thead>
              <tr>
                {b.rows[0]!.map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.slice(1).map((r, k) => (
                <tr key={k}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ),
      )}
    </>
  );
}
